import { useCallback, useEffect, useState } from "react";
import { formatTry } from "../../shared/pricing";

interface AdminIdentity {
  email: string;
  roles: Array<"moderator" | "refund_admin" | "product_owner">;
}

interface QueueReport {
  id: string;
  reason: string;
  severity: "urgent" | "standard";
  status: string;
  due_at: string;
  sla_status: "within" | "overdue";
  city_code: string;
  city_name: string;
  message_text: string;
  visibility_status: string;
  soft_hidden_by_signal: number;
}

interface CaseDetail {
  report: QueueReport & { context: string | null; message_id: string };
  history: Array<{
    id: string;
    version: number;
    message_text: string;
    visibility_status: string;
    amount_minor: number;
    published_at: string;
  }>;
  audit: Array<{
    id: string;
    actor_email: string;
    action_type: string;
    reason: string;
    created_at: string;
  }>;
}

interface RefundQueueItem {
  id: string;
  city_code: string;
  reason: string;
  amount_minor: number;
  status: string;
  reconciliation_reason: string | null;
}

interface PilotMetric {
  id: string;
  label: string;
  numerator: number;
  denominator: number | null;
  value: number;
  unit: "count" | "ratio";
  threshold: { label: string } | null;
  passed: boolean | null;
  period: { start: string | null; end: string };
  definition: string;
  exclusions: string[];
  includes: {
    editorialStarters: boolean;
    testPayments: boolean;
    refundedPayments: boolean;
    removedPublications: boolean;
  };
}

interface PilotDashboard {
  generatedAt: string;
  dataFreshness: string | null;
  gates: {
    demand: boolean;
    rivalryAndRepeat: boolean;
    safety: boolean;
    expansionRecommended: boolean;
  };
  metrics: PilotMetric[];
}

async function jsonRequest<T>(url: string, init?: RequestInit) {
  const response = await fetch(url, init);
  if (!response.ok) throw new Error(`${response.status}`);
  return (await response.json()) as T;
}

export function AdminPage() {
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [reports, setReports] = useState<QueueReport[]>([]);
  const [selected, setSelected] = useState<CaseDetail | null>(null);
  const [refunds, setRefunds] = useState<RefundQueueItem[]>([]);
  const [queue, setQueue] = useState<"all" | "urgent" | "standard">("all");
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [dashboard, setDashboard] = useState<PilotDashboard | null>(null);

  const loadReports = useCallback(async (selectedQueue: typeof queue) => {
    const query = selectedQueue === "all" ? "" : `?queue=${selectedQueue}`;
    const result = await jsonRequest<{ reports: QueueReport[] }>(`/api/admin/reports${query}`);
    setReports(result.reports);
  }, []);

  const loadRefunds = useCallback(async () => {
    const result = await jsonRequest<{ refunds: RefundQueueItem[] }>("/api/admin/refunds");
    setRefunds(result.refunds);
  }, []);

  const loadDashboard = useCallback(async () => {
    setDashboard(await jsonRequest<PilotDashboard>("/api/admin/pilot-dashboard"));
  }, []);

  useEffect(() => {
    const initial = window.setTimeout(() => {
      void jsonRequest<AdminIdentity>("/api/admin/me")
        .then(async (me) => {
          setIdentity(me);
          if (me.roles.includes("moderator")) await loadReports("all");
          if (me.roles.includes("refund_admin")) await loadRefunds();
          if (me.roles.includes("product_owner")) await loadDashboard();
        })
        .catch(() => setError("Cloudflare Access oturumu veya yetkili rol bulunamadı."));
    }, 0);
    return () => window.clearTimeout(initial);
  }, [loadDashboard, loadRefunds, loadReports]);

  async function openCase(reportId: string) {
    setSelected(await jsonRequest<CaseDetail>(`/api/admin/reports/${reportId}`));
  }

  async function moderate(action: string) {
    if (!selected || reason.trim().length < 3) return;
    await jsonRequest(`/api/admin/reports/${selected.report.id}/actions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, reason })
    });
    setReason("");
    setSelected(null);
    await loadReports(queue);
  }

  if (error) {
    return (
      <main className="admin-shell admin-shell--locked">
        <a className="wordmark" href="/">
          Şehrin Sesi
        </a>
        <h1>Yönetim erişimi gerekli.</h1>
        <p>{error} Cloudflare Access üzerinden MFA ile giriş yapıp yeniden dene.</p>
      </main>
    );
  }

  if (!identity) return <main className="admin-shell">Yönetim alanı doğrulanıyor…</main>;

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <a className="wordmark" href="/">
            Şehrin Sesi
          </a>
          <span> / Operasyon</span>
        </div>
        <div>
          <strong>{identity.email}</strong>
          <small>{identity.roles.join(" · ")}</small>
        </div>
      </header>

      {identity.roles.includes("product_owner") && dashboard && (
        <section className="admin-section pilot-section">
          <div className="pilot-heading">
            <div>
              <p className="eyebrow">30 günlük pilot kapıları</p>
              <h1>
                {dashboard.gates.expansionRecommended ? "Genişlemeye hazır." : "Kanıt bekleniyor."}
              </h1>
            </div>
            <button type="button" className="secondary-button" onClick={() => void loadDashboard()}>
              Metrikleri yenile
            </button>
          </div>
          <div className="pilot-gates" aria-label="Pilot kapıları">
            {(
              [
                ["Talep", dashboard.gates.demand],
                ["Rekabet ve tekrar", dashboard.gates.rivalryAndRepeat],
                ["Güvenlik", dashboard.gates.safety]
              ] as const
            ).map(([label, gatePassed]) => (
              <span className={gatePassed ? "gate gate--pass" : "gate gate--hold"} key={label}>
                {gatePassed ? "Geçti" : "Bekle"} · {label}
              </span>
            ))}
          </div>
          <p className="pilot-freshness">
            Veri tazeliği: {dashboard.dataFreshness ?? "Henüz olay yok"} · Hesaplandı:{" "}
            {dashboard.generatedAt}
          </p>
          <div className="metric-grid">
            {dashboard.metrics.map((metric) => (
              <article className="metric-card" key={metric.id}>
                <div className="metric-card__topline">
                  <span>{metric.label}</span>
                  {metric.passed !== null && (
                    <strong className={metric.passed ? "metric-pass" : "metric-hold"}>
                      {metric.passed ? "GEÇTİ" : "BEKLİYOR"}
                    </strong>
                  )}
                </div>
                <div className="metric-value">
                  {metric.unit === "ratio"
                    ? new Intl.NumberFormat("tr-TR", {
                        style: "percent",
                        maximumFractionDigits: 1
                      }).format(metric.value)
                    : metric.value}
                </div>
                <p>{metric.definition}</p>
                <dl>
                  <div>
                    <dt>Dönem</dt>
                    <dd>
                      {metric.period.start ?? "Anlık"} → {metric.period.end}
                    </dd>
                  </div>
                  <div>
                    <dt>Pay / payda</dt>
                    <dd>
                      {metric.numerator} / {metric.denominator ?? "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Eşik</dt>
                    <dd>{metric.threshold?.label ?? "Bilgilendirme"}</dd>
                  </div>
                  <div>
                    <dt>Dahil</dt>
                    <dd>
                      İade edilmiş: {metric.includes.refundedPayments ? "evet" : "hayır"};
                      kaldırılmış: {metric.includes.removedPublications ? "evet" : "hayır"}
                    </dd>
                  </div>
                  <div>
                    <dt>Hariç</dt>
                    <dd>{metric.exclusions.join("; ")}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        </section>
      )}

      {identity.roles.includes("moderator") && (
        <section className="admin-section">
          <div className="admin-section__title">
            <div>
              <p className="eyebrow">Moderasyon</p>
              <h1>Rapor kuyruğu</h1>
            </div>
            <div className="queue-tabs">
              {(["all", "urgent", "standard"] as const).map((item) => (
                <button
                  key={item}
                  className={queue === item ? "is-active" : ""}
                  onClick={() => {
                    setQueue(item);
                    void loadReports(item);
                  }}
                  type="button"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <div className="admin-grid">
            <div className="report-list">
              {reports.map((report) => (
                <button key={report.id} type="button" onClick={() => void openCase(report.id)}>
                  <span className={`severity severity--${report.severity}`}>{report.severity}</span>
                  <strong>{report.city_name}</strong>
                  <p>{report.message_text}</p>
                  <small className={report.sla_status === "overdue" ? "overdue" : ""}>
                    SLA {report.sla_status} · {report.due_at}
                  </small>
                </button>
              ))}
              {!reports.length && <p className="empty-queue">Açık rapor yok.</p>}
            </div>

            <article className="case-detail">
              {selected ? (
                <>
                  <p className="eyebrow">
                    {selected.report.city_code} · {selected.report.reason}
                  </p>
                  <h2>Vaka detayı</h2>
                  <blockquote>{selected.report.message_text}</blockquote>
                  <p>{selected.report.context ?? "Ek bağlam verilmedi."}</p>
                  <label htmlFor="moderation-reason">Karar gerekçesi</label>
                  <textarea
                    id="moderation-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={3}
                  />
                  <div className="case-actions">
                    <button onClick={() => void moderate("close")} type="button">
                      Kapat
                    </button>
                    <button onClick={() => void moderate("soft_hide")} type="button">
                      Yumuşak gizle
                    </button>
                    <button onClick={() => void moderate("restore")} type="button">
                      Geri getir
                    </button>
                    <button onClick={() => void moderate("remove_buyer_violation")} type="button">
                      İhlal: kaldır
                    </button>
                    <button onClick={() => void moderate("remove_platform_error")} type="button">
                      Platform hatası
                    </button>
                    <button onClick={() => void moderate("remove_moderation_error")} type="button">
                      Moderasyon hatası
                    </button>
                    <button onClick={() => void moderate("block")} type="button">
                      Payer engelle
                    </button>
                  </div>
                  <h3>Şehir geçmişi</h3>
                  <ol className="history-list">
                    {selected.history.map((item) => (
                      <li key={item.id}>
                        <strong>v{item.version}</strong>
                        <span>{item.message_text}</span>
                        <small>
                          {item.visibility_status} · {formatTry(BigInt(item.amount_minor))}
                        </small>
                      </li>
                    ))}
                  </ol>
                  <h3>Değiştirilemez denetim izi</h3>
                  <ol className="audit-list">
                    {selected.audit.map((item) => (
                      <li key={item.id}>
                        <strong>{item.action_type}</strong>
                        <span>{item.actor_email}</span>
                        <small>
                          {item.reason} · {item.created_at}
                        </small>
                      </li>
                    ))}
                  </ol>
                </>
              ) : (
                <div className="case-placeholder">
                  <h2>Bir vaka seç.</h2>
                  <p>Mesaj geçmişi, SLA ve denetim izi burada açılır.</p>
                </div>
              )}
            </article>
          </div>
        </section>
      )}

      {identity.roles.includes("refund_admin") && (
        <section className="admin-section refund-section">
          <p className="eyebrow">Finansal ayrım</p>
          <h2>İade kuyruğu</h2>
          {refunds.map((refund) => (
            <div className="refund-row" key={refund.id}>
              <span>{refund.city_code}</span>
              <strong>{formatTry(BigInt(refund.amount_minor))}</strong>
              <small>
                {refund.reason} · {refund.status}
              </small>
              <button
                type="button"
                disabled={refund.status !== "requested"}
                onClick={() =>
                  void jsonRequest(`/api/admin/refunds/${refund.id}/approve`, {
                    method: "POST"
                  }).then(loadRefunds)
                }
              >
                Tam iadeyi onayla
              </button>
            </div>
          ))}
        </section>
      )}
    </main>
  );
}
